import { computed, DestroyRef, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslocoService } from "@jsverse/transloco";
import { UserServiceProxy } from "@api/core";
import { isUnlimitedQuantity } from "@subscription/user-feature-manager-model";
import { finalize } from "rxjs";
import { TRANSCRIPTION_MONTHLY_LIMIT_FEATURE } from "../transcription.model";

/** Fraction of the monthly quota left at/under which we nudge the user (e.g. 1 of 3, 6 of 30). */
const NEAR_LIMIT_RATIO = 0.2;

/**
 * Monthly transcription quota state, shared by the shell (recording gate) and the toolbar alert.
 * Provided per-consumer (not root) so it can resolve the non-root `UserServiceProxy`.
 */
@Injectable()
export class TranscriptionUsageService {
    private readonly _userProxy = inject(UserServiceProxy);
    private readonly _t = inject(TranslocoService);
    private readonly _destroyRef = inject(DestroyRef);

    private readLimit(): number {
        return Number(abp.features.getValue(TRANSCRIPTION_MONTHLY_LIMIT_FEATURE)) || 0;
    }

    readonly limit = signal(this.readLimit());
    readonly used = signal(0);
    readonly loaded = signal(false);

    readonly featureActive = computed(() => this.limit() > 0);
    readonly isUnlimited = computed(() => isUnlimitedQuantity(this.limit()));
    readonly remaining = computed(() => Math.max(0, this.limit() - this.used()));
    readonly isExhausted = computed(() => this.featureActive() && !this.isUnlimited() && this.used() >= this.limit());
    /** Quota is exhausted or within the final NEAR_LIMIT_RATIO slice — the only time we surface the alert. */
    readonly isNearLimit = computed(() => {
        if (!this.featureActive() || this.isUnlimited()) {
            return false;
        }
        const threshold = Math.max(1, Math.ceil(this.limit() * NEAR_LIMIT_RATIO));
        return this.remaining() <= threshold;
    });
    readonly quotaLabel = computed(() =>
        this.isUnlimited()
            ? this._t.translate("patient-transcriptions.quota-unlimited")
            : this._t.translate("patient-transcriptions.quota-remaining", {
                  remaining: this.remaining(),
                  limit: this.limit(),
              })
    );

    reload(): void {
        this.limit.set(this.readLimit());
        if (!this.featureActive() || this.isUnlimited()) {
            this.loaded.set(true);
            return;
        }
        this._userProxy
            .getTenantFeaturesUsage(TRANSCRIPTION_MONTHLY_LIMIT_FEATURE)
            .pipe(
                takeUntilDestroyed(this._destroyRef),
                finalize(() => this.loaded.set(true))
            )
            .subscribe({
                next: (rows) => {
                    const row = rows?.find((r) => r.name === TRANSCRIPTION_MONTHLY_LIMIT_FEATURE);
                    this.used.set(row ? Number(row.value) || 0 : 0);
                },
                // Usage unknown ⇒ fail safe to the limit so the start gate blocks instead of letting the
                // user begin a session the backend (CreateTranscriptionCommandHandler) would reject anyway.
                error: () => this.used.set(this.limit()),
            });
    }
}
